import { supabase } from "@/integrations/supabase/client";

export interface ShippingRate {
  id: string;
  zone_name: string;
  countries: string[];
  min_weight_kg: number;
  max_weight_kg: number | null;
  base_price: number;
  price_per_kg: number;
  free_shipping_threshold: number | null;
}

export interface ShippingCalculation {
  shippingCost: number;
  zoneName: string;
  isFreeShipping: boolean;
  freeShippingThreshold: number | null;
}

// Calculate shipping from database rates
export async function calculateShipping(
  totalWeight: number,
  country: string,
  orderSubtotal: number
): Promise<ShippingCalculation | null> {
  try {
    // Fetch shipping rates from database
    const { data: rates, error } = await (supabase
      .from('shipping_rates' as any)
      .select('*')
      .eq('is_active', true)) as any;

    if (error || !rates || rates.length === 0) {
      console.error('Failed to fetch shipping rates:', error);
      return null;
    }

    // Find matching rate for country and weight
    const matchingRate = rates.find((rate: ShippingRate) => {
      const countryMatch = rate.countries.some((c: string) => 
        c.toLowerCase() === country.toLowerCase()
      );
      const weightMatch = totalWeight >= rate.min_weight_kg && 
        (rate.max_weight_kg === null || totalWeight < rate.max_weight_kg);
      return countryMatch && weightMatch;
    }) as ShippingRate | undefined;

    if (!matchingRate) {
      // Default to EU rate if no specific match
      const euRate = rates.find((rate: ShippingRate) => 
        rate.zone_name === 'EU' && 
        totalWeight >= rate.min_weight_kg && 
        (rate.max_weight_kg === null || totalWeight < rate.max_weight_kg)
      ) as ShippingRate | undefined;

      if (!euRate) {
        return null;
      }

      return calculateRateResult(euRate, totalWeight, orderSubtotal);
    }

    return calculateRateResult(matchingRate, totalWeight, orderSubtotal);
  } catch (error) {
    console.error('Error calculating shipping:', error);
    return null;
  }
}

function calculateRateResult(
  rate: ShippingRate,
  totalWeight: number,
  orderSubtotal: number
): ShippingCalculation {
  // Check for free shipping
  if (rate.free_shipping_threshold && orderSubtotal >= rate.free_shipping_threshold) {
    return {
      shippingCost: 0,
      zoneName: rate.zone_name,
      isFreeShipping: true,
      freeShippingThreshold: rate.free_shipping_threshold,
    };
  }

  // Calculate shipping cost
  let shippingCost = rate.base_price;
  
  // Add per-kg price for weight above minimum
  if (rate.price_per_kg > 0 && totalWeight > rate.min_weight_kg) {
    const extraWeight = totalWeight - rate.min_weight_kg;
    shippingCost += extraWeight * rate.price_per_kg;
  }

  return {
    shippingCost: Math.round(shippingCost * 100) / 100,
    zoneName: rate.zone_name,
    isFreeShipping: false,
    freeShippingThreshold: rate.free_shipping_threshold,
  };
}

export function getTotalCartWeight(items: Array<{ quantity: number; weight?: number }>): number {
  return items.reduce((total, item) => {
    const itemWeight = item.weight || 0;
    return total + (itemWeight * item.quantity);
  }, 0);
}
